/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { UserMin } from '../constants/users';
import { UniversalTrackerValuePlain } from './surveyScope';
import { UniversalTrackerPlain } from './universalTracker';
import { StakeholderGroup } from '../model/stakeholderGroup';
import { Action } from '../constants/action';
import { HistoryDocument } from './document';
import { SurveyActionData, SurveyModelMinData } from '../model/surveyData';
import { AssuranceRoles } from '../constants/assurance';
import { InitiativePlain } from './initiative';

export enum AssuranceStatus {
  Created = 'created',
  Processing = 'processing',
  /**
   * Utrv was updated, need to reset
   */
  Pending = 'pending',

  /** Assured, but underlying utrv was updated after completing assurance portfolio  **/
  PendingAfterPortfolioComplete = 'pending_after_portfolio_complete',
  Disabled = 'disabled',
  Removed = 'removed',
  Completed = 'completed',
  Rejected = 'rejected',
}

export enum AssurancePortfolioStatus {
  Created = 'created',
  Pending = 'pending',
  Processing = 'processing',
  Deleted = 'deleted',
  Completed = 'completed',
}


export interface AssuranceValueHistory {
  documents: any[];
  _id: string;
  action: string;
  utrvHistoryIndex: number;
  userId: string;
  created: Date;
}

export interface UniversalTrackerValueAssurance {
  _id: string;
  utrvId: string;
  utrvHistoryIndex: number;
  stakeholders: StakeholderGroup;
  status: AssuranceStatus;
  history: AssuranceValueHistory[];
  portfolioId: string;
  created: Date;
  permissions?: AssurancePermissionType<UtrvAssurancePermissions>[];
  utrv?: Pick<UniversalTrackerValuePlain, '_id' | 'universalTrackerId' | 'status'>;
}

export interface UtrvAssurancePopulated extends UniversalTrackerValueAssurance {
  utrv: UniversalTrackerValuePlain;
}

export interface SurveyMin {
  _id: string;
  name: string;
  code: string;
  sourceName: string;
  effectiveDate: string;
}

export interface AssurancePorfolioHistory {
  _id: string;
  action: string;
  userId: string;
  created: Date;
  documents: any[];
}

export interface Organization {
  _id: string;
  partnerTypes: string[];
  name: string;
  organizationType: string;
  profile: string;
}

interface LeanDocument {
  documentId: string,
  type: string;
}

export enum UtrvAssurancePermissions {
  AssurerFollower = 'assurer_follower'
}

export interface AssurancePermissionType<T> {
  userId: string,
  permissions: T[],
}

export interface AssurancePortfolio {
  _id: string;
  status: AssurancePortfolioStatus;
  portfolioType: string;
  organizationId: string;
  title?: string;
  description: string;
  initiativeId: string;
  surveyId: string;
  stakeholders: StakeholderGroup;
  history: AssurancePorfolioHistory[];
  documents: LeanDocument[];
  created: string;
  users: UserMin[];
  historyDocuments: HistoryDocument[];
  universalTrackerValueAssurances: UniversalTrackerValueAssurance[];
  organization: Organization;
  survey: Pick<SurveyModelMinData, '_id' | 'code' | 'name' | 'sourceName' | 'effectiveDate'> & { deletedDate: string };
  initiative?: Pick<InitiativePlain, '_id' | 'name'>;
  assurers: AssurancePortfolioAssurer[];
}

export interface OrganizationAssurancePortfolio extends AssurancePortfolio {
  surveyData: SurveyActionData;
}

export interface AssurancePortfolioStakeholder {
  _id: string;
  firstName: string;
  surname: string;
}

export interface AssurancePortfolioAssurer {
  _id?: string;
  firstName?: string;
  surname?: string;
  email?: string;
  active: boolean;
  isAdmin: boolean;
  isUser: boolean;
}

export type AssuranceDocumentType = 'basis_of_reporting' | 'management_statement' | 'assurance_statement';

export interface AssuranceListPortfolio {
  _id: string;
  status: AssuranceStatus;
  description: string;
  documents: { documentId: string, type: AssuranceDocumentType }[];
  universalTrackerValueAssurances: UniversalTrackerValueAssurance[];
  universalTrackerValues: {
    _id: string;
    universalTrackerid: string;
    initiativeId: string;
    status: string;
  }[];
  survey: {
    _id: string;
    name: string;
    effectiveDate: string;
  };
  initiative: {
    _id: string;
    name: string;
  }
  stakeholders: AssurancePortfolioStakeholder[]
  assurers: AssurancePortfolioAssurer[]
}

export interface AssuranceStakeholdersData extends StakeholderGroup {
  _id: string;
  stakeholders: StakeholderGroup;
  organizationId: string,
  users: AssurancePortfolioStakeholder[],
  organization: Organization;
}

export interface AssuranceQuestionList {
  utr: UniversalTrackerPlain;
  utrv: UniversalTrackerValuePlain;
  status: AssuranceStatus,
  assuranceId: string;
  name: string;
  valueType: string;
}

export type UpdateStakeholderData =
  { userId?: string, email: string, action: Action } |
  { userId: string, email?: string, action: Action };

export type UpdatePermissionData =
  { userId?: string, email: string, action: Action, role: string } |
  { userId: string, email?: string, action: Action, role: string }

export interface AssurancePortfolioCommon {
  title?: string;
  description?: string;
  initiativeId: string;
  surveyId?: string;
  organizationId: string;
  portfolioType: string;
}

export interface AssurancePortfolioCreate extends AssurancePortfolioCommon {
  assurancePrimaryContactId: string;
}

export interface AssurancePortfolioUpdate extends Omit<AssurancePortfolioCreate, 'initiativeId' | 'surveyId'> {
  portfolioId: string,
  files?: { [key: string]: unknown };
}

export interface AssurancePortfolioComplete {
  files: File[];
  questions: string[];
}

export interface AssurancePermissionsData extends StakeholderGroup {
  _id: string;
  organizationId: string;
  initiativeId: string;
  stakeholders: StakeholderGroup;
  assurers: AssurancePortfolioAssurer[];
  organization: Organization;
}

export interface AssurancePermissions {
  assurers: {
    [AssuranceRoles.ADMIN]: AssurancePortfolioAssurer[],
    [AssuranceRoles.USER]: AssurancePortfolioAssurer[],
  }
  organizationId: string;
  portfolioId: string;
}
